import React, {FC} from 'react'
import {useSelector} from 'react-redux'
import {LocationDetails} from 'components/HotelCardLocationDetails'
import {
  PropertyHighlights,
  usePropertyHighlights
} from 'components/PropertyHighlights'
import {TaxationAndPriceMessage} from 'components/TaxationAndPriceMessage'
import {
  getHotel,
  getHotelAvailabilityPrices,
  getLengthOfStay,
  getNumberOfRooms,
  getSplitBookingDetails,
  State
} from 'modules/sapiSearch/selectors'
import {SelectedElement} from 'utils/selectedElement'

import {Box} from '@vio/atlas/helpers/Box'
import {StarRating} from '@vio/atlas/StarRating'
import {HotelName} from '@vio/shared/src/booking/HotelInfoCard/styles'
import {ReviewRating} from '@vio/shared/src/search/ReviewRating'

import {HotelBadges} from '../../HotelCard/HotelBadges/HotelBadges'
import {TOP_SECTION_MIN_HEIGHT} from '../HotelCardLayout/HotelCardLayout'
import {PropertySummaryLoading} from './PropertySummaryLoading'

interface Props {
  hotelId: string
  isCompactHotelCard?: boolean
}

const PropertySummaryComponent: FC<Props> = ({hotelId, isCompactHotelCard}) => {
  const hotelDetails = useSelector((state: State) => getHotel(state, hotelId))

  const hotelHasAvailabilityPrices = Boolean(
    useSelector(state => getHotelAvailabilityPrices(state, hotelId))
  )
  const shouldShowTaxationAndNightlyPrice = !(
    isCompactHotelCard && !hotelHasAvailabilityPrices
  )

  // Added as part of 32cf4c25-srp-room-count-copy experiment
  const {showSplitBookingLoader, hasPrimarySplitBookingOffer} = useSelector(
    state => getSplitBookingDetails(state, hotelId)
  )
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const numberOfNights = useSelector(getLengthOfStay)
  // Added as part of dcbc5616-property-highlights-v1
  const highlights = usePropertyHighlights(hotelId)

  // Showing skeleton
  if (!hotelDetails || !hotelId) return <PropertySummaryLoading />

  return (
    <>
      <Box
        flexDirection="column"
        display="flex"
        flexWrap="wrap"
        minHeight={
          shouldShowTaxationAndNightlyPrice
            ? `${TOP_SECTION_MIN_HEIGHT - 20}px`
            : undefined
        }
        boxSizing="border-box"
        flexBasis="100%"
        justifyContent="space-between"
      >
        <div>
          {/* Highlights replace the badges. Added as part of dcbc5616-property-highlights-v1 */}
          {!highlights.show && <HotelBadges hotelId={hotelId} />}
          <div data-id={SelectedElement.StarRating}>
            <StarRating
              wrapperId={`HotelCardMobileStarRating-${hotelId}`}
              rating={hotelDetails.starRating}
              size="xs"
            />
          </div>
          <HotelName
            id={`HotelCardMobileHotelName-${hotelId}`}
            data-id={SelectedElement.HotelName}
          >
            {hotelDetails.hotelName}
          </HotelName>

          <Box data-id={SelectedElement.Location} marginBottom="s300">
            <LocationDetails placeName={hotelDetails.placeDisplayName} />
            {/* Added as part of dcbc5616-property-highlights-v1 */}
            <PropertyHighlights items={highlights.location} />
          </Box>

          <div data-id={SelectedElement.ReviewRating}>
            <ReviewRating
              rating={hotelDetails.guestRating.overall}
              totalRatingCount={hotelDetails.reviewCount}
              hotelId={hotelDetails.objectID}
            />
          </div>
          {/* Added as part of dcbc5616-property-highlights-v1 */}
          {highlights.themes.length > 0 && (
            <Box paddingTop="s300">
              <PropertyHighlights items={highlights.themes} />
            </Box>
          )}
        </div>

        {shouldShowTaxationAndNightlyPrice && (
          <Box
            alignSelf="flex-end"
            textAlign="end"
            marginTop="s500"
            paddingRight="s300"
          >
            <TaxationAndPriceMessage
              hotelId={hotelId}
              isSplitBooking={
                hasPrimarySplitBookingOffer && !showSplitBookingLoader
              }
              numberOfRooms={numberOfRooms}
              numberOfNights={numberOfNights}
            />
          </Box>
        )}
      </Box>
    </>
  )
}

export const PropertySummary = React.memo(PropertySummaryComponent)
